import { getLogoutRedirectUrl } from '@white-label-helper/get-logout-redirect-url';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import { getCookieDomain } from '@white-label-helper/get-cookie-domain';
import { getUrlParam, isDedicatedFlow } from '@white-label-helper/dedicated-flow';
import { isBookingPortalFlow } from '@white-label-helper/booking-portal-flow';
import { COOKIES_KEY_PREFIX } from '../../constants';

import {
  handleRedirectCallback,
  clearStorageForInactiveSessionWrapper,
  createAuthClientWrapper,
  loginWrapper,
  logoutWrapper,
  checkSSOSessionWrapper,
  fetchUserWrapper,
  setUserTokenWrapper,
  getTokenWrapper,
  refreshUserWrapper
} from '../auth-flow-helpers';

// Types
import type { CustomAuth0Settings, CustomAuthFlowOptions } from '@white-label-types/auth';

/**
 * Auth plugin for Nuxt using Auth0.
 *
 * @param {Context} context
 */
const initAuthDefaultFlow = (
  {
    app,
    $config,
    route
  }: CustomAuthFlowOptions
) => {
  return {
    isAuthenticated: false,
    user: null,
    auth0Client: null,
    clientId: '',
    domain: '',
    audience: '',
    returnUrl: getLogoutRedirectUrl(), // store in the options and get as a cookie or localStorage
    channelToken: '',

    /**
     * Initializes the Auth0 client and handles sessions.
     * @async
     * @returns {Promise<void>}
     */
    async initAuth() {
      // default values
      this.clientId = app['$partnerConfig']?.auth0_client_id || getAppVariable('auth0_client_id');
      this.domain = $config?.['AUTH0_DOMAIN'];
      this.audience = $config?.['AUTH0_AUDIENCE'];

      const isPartnerGroupSetupEnabled = getFeatureFlag('MC_63_PARTNER_GROUP_SETUP');
      const authorizationParams: {
        redirect_uri: string;
        audience: string;
        scope: string;
        organization?: string;
      } = {
        redirect_uri: `${window.location.origin}/callback`,
        audience: this.audience,
        scope: 'openid profile email',
      };

      if (isPartnerGroupSetupEnabled) {
        authorizationParams.organization = getAppVariable('auth0.organization_id');
      }

      const options = {
        isAuthenticated: this.isAuthenticated,
        domain: this.domain,
        clientId: this.clientId,
        cacheLocation: 'localstorage' as const,
        cookieDomain: getCookieDomain(),
        authorizationParams,
        returnUrl: this.returnUrl,
        channelToken: this.channelToken,
      }

      if (isDedicatedFlow(route) || isBookingPortalFlow()) {
        // Set dedicated flow Auth0 client values if coming from Admin or Partner extranet's
        this.isAuthenticated = app['$cookies'].get(`${COOKIES_KEY_PREFIX}.${this.clientId}.is.authenticated`) === 'true';
        this.clientId = $config['AUTH0_PARTNER_CLIENT_ID'];
        this.domain = $config['AUTH0_PARTNER_DOMAIN'];
        this.audience = $config['AUTH0_PARTNER_AUDIENCE'];
        this.returnUrl = getUrlParam(route, 'returnUrl') || app['$cookies'].get(`${COOKIES_KEY_PREFIX}.${this.clientId}.returnUrl`);
        this.channelToken = app['$cookies'].get(`${COOKIES_KEY_PREFIX}.${this.clientId}.channelToken`) || '';
        options.isAuthenticated = this.isAuthenticated;
        options.clientId = this.clientId;
        options.domain = this.domain;
        options.authorizationParams.audience = this.audience;
        options.returnUrl = this.returnUrl;
        options.channelToken = this.channelToken;
      }

      await createAuthClientWrapper(
        this,
        options
      );

      await handleRedirectCallback(app, this);
    },
    /**
     * Redirects the user to the Auth0 login page.
     * @async
     * @param {CustomAuth0Settings} customAuth0Settings - Custom brand settings for Auth0 login page
     */
    async login(customAuth0Settings: CustomAuth0Settings): Promise<void> {
      await loginWrapper(this.auth0Client, customAuth0Settings);
    },

    /**
     * Logs the user out and clears cookies.
     * @param {string} [returnTo] - Optional return URL after logout.
     */
    async logout(returnTo?: string): Promise<void> {
      if (isBookingPortalFlow() || isDedicatedFlow(route)) {
        app['$cookies'].removeAll({ path: '/', domain: getCookieDomain() });
        window.localStorage.clear();
        window.sessionStorage.clear();
      }
      await logoutWrapper(this.auth0Client, returnTo || this.returnUrl);
    },

    /**
     * Refreshes user information and authentication state.
     * @async
     */
    async refreshUser(): Promise<void> {
     await refreshUserWrapper(this.auth0Client, this)
    },

    clearStorageForInactiveSession() {
      clearStorageForInactiveSessionWrapper(
        app, this
      );
      if (!isBookingPortalFlow()) window.location.href = '/';
    },
    /**
     * Checks the SSO session (dedicated flows e.g booking portal) and restores the user state if active.
     * @async
     * @returns {Promise<void>}
     */
    async checkSSOSession() {
      await checkSSOSessionWrapper(
        app,
        this
      );
    },

    /**
     * Fetches user details from Auth0's userinfo endpoint. Needed for SSO sessions
     * (dedicated flows e.g booking portal) to fetch user data manually
     *
     * @async
     * @param {string} token - The Auth0 access token.
     * @returns {Promise<void>}
     */
    async fetchUser(token: string) {
     await fetchUserWrapper(
        token,
        app,
        this
      )
    },

    /**
     * Sets tokens in localStorage and cookies for SSO session management
     * (dedicated flows e.g booking portal).
     * @param {string} token - The Auth0 access token.
     * @param {number} tokenExpiration - The token expiration.
     * @param {string|null} channelToken - The channel token.
     */
    setUserToken(token: string, tokenExpiration: number, channelToken: string) {
      setUserTokenWrapper(
        token,
        tokenExpiration,
        channelToken,
        app,
        this,
      )
    },

    /**
     * Retrieves a token.
     * @async
     * @returns {Promise<string|null>} The token prefixed with "Bearer" or null if no token is available.
     */
    async getToken(): Promise<string | null> {
      return getTokenWrapper(this.auth0Client);
    },
  }
}

export { initAuthDefaultFlow }
